import { useEffect, useRef, useState } from 'react';
import { hasScope } from '@nyce/admin-kit/api';
import { Button, Chip, Dialog, Select, Switch, TextField } from '@nyce/admin-kit/ui';
import { SCOPES } from '../../api';
import {
  ActionsRow,
  ConfirmButton,
  EmptyState,
  ErrorBox,
  Loading,
  Page,
  TableScroller,
  TagsField,
  useAsync,
  useNotice,
} from '../../components/common';
import {
  stats,
  type Frequency,
  type ReportRequest,
  type Subscription,
  type SubscriptionInput,
} from '../../api/stats';
import { formatDateTime } from '../../lib/format';
import { useHubLogin } from '../../session';
import { OrgLink, statsError, useParam } from './shared';
import './statistics.css';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function scheduleText(
  s: Pick<Subscription, 'frequency' | 'scheduleTime' | 'scheduleDay' | 'scheduleDays'>
): string {
  if (s.frequency === 'weekly') return `${DAYS[(s.scheduleDay || 1) - 1]} ${s.scheduleTime}`;
  if (s.frequency === 'monthly') return `day ${s.scheduleDay} ${s.scheduleTime}`;
  const days =
    s.scheduleDays?.length && s.scheduleDays.length < 7
      ? ` ${s.scheduleDays.map((d) => DAYS[d - 1]).join(', ')}`
      : '';
  return `daily${days} ${s.scheduleTime}`;
}

export function subscriptionProblems(v: SubscriptionInput): string[] {
  const p: string[] = [];
  if (!(v.customerId > 0)) p.push('organization (customer id) is required');
  if (!v.methods.length || v.methods.length > 50) p.push('1 to 50 methods');
  if (!v.recipients.length) p.push('at least one recipient');
  const bad = v.recipients.filter((r) => !EMAIL.test(r));
  if (bad.length) p.push(`not an e-mail address: ${bad.join(', ')}`);
  if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(v.scheduleTime)) p.push('time must be HH:mm');
  if (v.frequency === 'weekly' && !(v.scheduleDay >= 1 && v.scheduleDay <= 7)) p.push('weekday 1–7');
  if (v.frequency === 'monthly' && !(v.scheduleDay >= 1 && v.scheduleDay <= 31)) p.push('day of month 1–31');
  return p;
}

function SubscriptionDialog({
  initial,
  fixedCustomer,
  onSaved,
  onClose,
}: {
  initial: Subscription | null;
  fixedCustomer?: { id: number; name: string | null };
  onSaved: (s: Subscription) => void;
  onClose: () => void;
}) {
  const [v, setV] = useState<SubscriptionInput>(() =>
    initial
      ? (({ id: _i, lastRun: _l, createdAt: _c, updatedAt: _u, ...rest }) => rest)(initial)
      : {
          customerId: fixedCustomer?.id ?? 0,
          customerName: fixedCustomer?.name ?? '',
          methods: [],
          recipients: [],
          workserver: 'ALL',
          frequency: 'weekly',
          scheduleTime: '06:00',
          scheduleDay: 1,
          scheduleDays: [],
          timezone: 'Europe/Stockholm',
          enabled: true,
        }
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = <K extends keyof SubscriptionInput>(k: K, value: SubscriptionInput[K]) =>
    setV((cur) => ({ ...cur, [k]: value }));
  const problems = subscriptionProblems(v);
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      onSaved(initial ? await stats.updateSubscription(initial.id, v) : await stats.createSubscription(v));
      onClose();
    } catch (e) {
      setError(statsError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={initial ? `Report subscription ${initial.id}` : 'New report subscription'}
      maxWidth={760}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={busy} disabled={problems.length > 0} onClick={() => void save()}>
            Save
          </Button>
        </>
      }
    >
      <ErrorBox error={error} />
      <div className="nyk-form">
        <TextField
          label="Customer id"
          inputMode="numeric"
          value={v.customerId ? String(v.customerId) : ''}
          disabled={!!fixedCustomer}
          onChange={(e) => set('customerId', Number(e.target.value.replace(/\D/g, '')) || 0)}
        />
        <TextField
          label="Customer name (report title)"
          value={v.customerName ?? ''}
          onChange={(e) => set('customerName', e.target.value)}
        />
        <div className="nyk-span2">
          <TagsField
            label="Methods"
            value={v.methods}
            onChange={(m) => set('methods', m)}
            placeholder="e.g. PerformOrderLinePickingInternal"
          />
        </div>
        <div className="nyk-span2">
          <TagsField
            label="Recipients"
            value={v.recipients}
            onChange={(r) => set('recipients', r)}
            placeholder="name@example.com"
          />
        </div>
        <TextField
          label="WorkServer"
          value={v.workserver}
          onChange={(e) => set('workserver', e.target.value)}
          hint="ALL = every WorkServer together"
        />
        <Select
          label="Frequency"
          value={v.frequency}
          onChange={(f) => set('frequency', f as Frequency)}
          options={[
            { value: 'daily', label: 'Daily' },
            { value: 'weekly', label: 'Weekly' },
            { value: 'monthly', label: 'Monthly' },
          ]}
        />
        <TextField
          label="Time (HH:mm, Europe/Stockholm)"
          value={v.scheduleTime}
          onChange={(e) => set('scheduleTime', e.target.value)}
        />
        {v.frequency === 'weekly' && (
          <Select
            label="Weekday"
            value={String(v.scheduleDay)}
            onChange={(d) => set('scheduleDay', Number(d))}
            options={DAYS.map((d, i) => ({ value: String(i + 1), label: d }))}
          />
        )}
        {v.frequency === 'monthly' && (
          <TextField
            label="Day of month"
            inputMode="numeric"
            value={String(v.scheduleDay)}
            onChange={(e) => set('scheduleDay', Number(e.target.value.replace(/\D/g, '')) || 0)}
            hint="beyond the month's end = last day"
          />
        )}
        {v.frequency === 'daily' && (
          <div className="nyk-ui-field">
            <span>Days (none = every day)</span>
            <div className="sx-links">
              {DAYS.map((d, i) => {
                const on = v.scheduleDays.includes(i + 1);
                return (
                  <Chip
                    key={d}
                    size="sm"
                    selected={on}
                    onClick={() =>
                      set(
                        'scheduleDays',
                        on ? v.scheduleDays.filter((x) => x !== i + 1) : [...v.scheduleDays, i + 1].sort()
                      )
                    }
                  >
                    {d}
                  </Chip>
                );
              })}
            </div>
          </div>
        )}
        <TextField
          label="Chart time zone (IANA)"
          value={v.timezone}
          onChange={(e) => set('timezone', e.target.value)}
        />
        <Switch checked={v.enabled} onChange={(e) => set('enabled', e)} label="Enabled" />
      </div>
      {problems.length > 0 && <p className="nyk-ui-hint">To save: {problems.join(' · ')}</p>}
    </Dialog>
  );
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** How often a queued preview / test send is polled (tests shorten it). */
export const REPORT_POLL = { ms: 2000 };

/** Saves a Blob as a file (the hub streams the PDF; it is never opened from a URL with a token). */
function saveBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Queue a preview or test send and poll it (the processor renders within seconds; gives up after 3 min). */
export function useReportRequests() {
  const [requests, setRequests] = useState<
    Record<number, ReportRequest | { status: 'queueing' } | undefined>
  >({});
  const alive = useRef(true);
  useEffect(
    () => () => {
      alive.current = false;
    },
    []
  );
  const toast = useNotice();
  const start = async (sub: Subscription, kind: 'preview' | 'send') => {
    setRequests((r) => ({ ...r, [sub.id]: { status: 'queueing' } }));
    try {
      let req = kind === 'preview' ? await stats.preview(sub.id) : await stats.testSend(sub.id);
      const until = Date.now() + 180_000;
      while (alive.current && (req.status === 'pending' || req.status === 'running') && Date.now() < until) {
        setRequests((r) => ({ ...r, [sub.id]: req }));
        await sleep(REPORT_POLL.ms);
        req = await stats.request(req.id);
      }
      setRequests((r) => ({ ...r, [sub.id]: req }));
      if (req.status === 'failed') toast.error(`Report ${sub.id}: ${req.detail ?? 'failed'}`);
      else if (req.status === 'ok' && kind === 'send') toast.success(`Test report ${sub.id} sent`);
      else if (req.status !== 'ok') toast.error(`Report ${sub.id} is still ${req.status}; try again later.`);
    } catch (e) {
      setRequests((r) => ({ ...r, [sub.id]: undefined }));
      toast.error(statsError(e));
    }
  };
  const download = async (sub: Subscription, req: ReportRequest) => {
    try {
      saveBlob(await stats.pdf(req.id), `report-${sub.customerName || sub.customerId}-${req.id}.pdf`);
    } catch (e) {
      toast.error(statsError(e));
    }
  };
  return { requests, start, download, toast };
}

/** Report subscriptions (all, or of one organization): CRUD for admins, preview for every reader. */
export function SubscriptionsPanel({ customer }: { customer?: { id: number; name: string | null } }) {
  const login = useHubLogin();
  const canAdmin = hasScope(login?.scopes, SCOPES.statsAdmin);
  const list = useAsync(() => stats.subscriptions(customer?.id), [customer?.id]);
  const [editing, setEditing] = useState<Subscription | 'new' | null>(null);
  const reports = useReportRequests();
  const remove = async (s: Subscription) => {
    try {
      await stats.deleteSubscription(s.id);
      reports.toast.success(`Deleted subscription ${s.id}`);
      await list.reload();
    } catch (e) {
      reports.toast.error(statsError(e));
    }
  };
  const items = list.data ?? [];
  return (
    <section className="sx-body" data-testid="subscriptions">
      {reports.toast.element}
      {canAdmin && (
        <div className="sx-toolbar">
          <span className="sx-spacer" />
          <Button onClick={() => setEditing('new')}>New subscription</Button>
        </div>
      )}
      <ErrorBox error={list.error} />
      <Loading loading={list.loading && !list.data} />
      <div className="nyk-card">
        <TableScroller>
          <table className="nyk-ui-table nyk-cards" data-testid="subscriptions-table">
            <thead>
              <tr>
                <th>Subscription</th>
                {!customer && <th>Organization</th>}
                <th>Schedule</th>
                <th>Recipients</th>
                <th>Methods</th>
                <th>Last sent</th>
              </tr>
            </thead>
            {items.map((s) => {
              const req = reports.requests[s.id];
              const pending =
                req && (req.status === 'queueing' || req.status === 'pending' || req.status === 'running');
              return (
                <tbody key={s.id} className="nyk-row-group" data-subscription={s.id}>
                  <tr className={s.enabled ? undefined : 'nyk-row-muted'}>
                    <td className="nyk-card-title">
                      #{s.id} · {s.workserver}
                      {!s.enabled && <span className="nyk-ui-hint"> · disabled</span>}
                    </td>
                    {!customer && (
                      <td data-label="Organization">
                        <OrgLink customerId={s.customerId} name={s.customerName} />
                      </td>
                    )}
                    <td data-label="Schedule">{scheduleText(s)}</td>
                    <td data-label="Recipients" className="nyk-wrap">
                      {s.recipients.join(', ')}
                    </td>
                    <td data-label="Methods" className="nyk-wrap">
                      {s.methods.length > 3
                        ? `${s.methods.slice(0, 3).join(', ')} +${s.methods.length - 3}`
                        : s.methods.join(', ')}
                    </td>
                    <td data-label="Last sent">{formatDateTime(s.lastRun)}</td>
                  </tr>
                  <ActionsRow colSpan={customer ? 5 : 6}>
                    {req && 'id' in req && req.status === 'ok' && req.hasPdf ? (
                      <Button size="sm" variant="ghost" onClick={() => void reports.download(s, req)}>
                        Download PDF
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        loading={!!pending}
                        onClick={() => void reports.start(s, 'preview')}
                      >
                        Preview
                      </Button>
                    )}
                    {canAdmin && (
                      <>
                        <ConfirmButton
                          size="sm"
                          variant="ghost"
                          message={`Send a test of report ${s.id} to ${s.recipients.join(', ')} now?`}
                          onConfirm={() => void reports.start(s, 'send')}
                        >
                          Test send
                        </ConfirmButton>
                        <Button size="sm" variant="ghost" onClick={() => setEditing(s)}>
                          Edit
                        </Button>
                        <ConfirmButton
                          size="sm"
                          variant="danger"
                          message={`Delete report subscription ${s.id}?`}
                          onConfirm={() => void remove(s)}
                        >
                          Delete
                        </ConfirmButton>
                      </>
                    )}
                  </ActionsRow>
                </tbody>
              );
            })}
          </table>
        </TableScroller>
        {list.data && !items.length && (
          <EmptyState
            title="No report subscriptions."
            hint={canAdmin ? 'Create one to e-mail a PDF on a schedule.' : undefined}
          />
        )}
      </div>
      {editing && (
        <SubscriptionDialog
          initial={editing === 'new' ? null : editing}
          fixedCustomer={customer}
          onSaved={(s) => {
            reports.toast.success(`Saved subscription ${s.id}`);
            void list.reload();
          }}
          onClose={() => setEditing(null)}
        />
      )}
    </section>
  );
}

export function ReportsPage() {
  const [customer] = useParam('customerId', '');
  return (
    <Page
      title="Reports"
      description="Scheduled PDF reports e-mailed by the datacollector's processor (schedules in Europe/Stockholm time)."
    >
      <SubscriptionsPanel customer={customer ? { id: Number(customer), name: null } : undefined} />
    </Page>
  );
}
